import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from "@angular/core";
import {ActivatedRoute, ParamMap} from "@angular/router";
import {BasePage} from "~/app/pages/base.page";
import {NetworkConnectionType} from "~/app/core/network.service";
import {InteractionComponent} from "~/app/core/model/interaction-component";
import {InteractionType} from "~/app/core/model/interaction-type";
import {CoreService} from "~/app/core/core.service";

@Component({
    selector: "Validate",
    templateUrl: "./validate.page.html",
    styleUrls: ["./validate.page.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ValidatePage extends BasePage implements OnInit {
    email: string = "";
    offerId: number = null;

    validateCodeText: string = "";

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly activatedRoute: ActivatedRoute,
        private readonly core: CoreService,
    ) {
        super();
        if (this.core.api.user_info()) this.email = this.core.api.user_info().email;
    }

    ngOnInit(): void {
        this.activatedRoute.queryParamMap.subscribe((value: ParamMap) => {
            if (value.has("offerId")) {
                this.offerId = Number(value.get("offerId"));
            }
            if (value.has("allowBack")) {
                this.allowBack = value.get("allowBack") === "true";
            }
        });
    }

    goBack() {
        if (!this.waiting) {
            this.waiting = true;
            this.waitingSpinning = false;
            if (this.allowBack) {
                this.core.nav.navBack();
            } else {
                this.core.nav.navPageSearching(this.core.api, this.offerId, false);
            }
        }
    }

    onValidate() {
        const validationCode = this.validateCodeText;
        if (this.core.net.connectionType === NetworkConnectionType.None) {
            this.handleNoData("validate");
        } else {
            this.waiting = true;
            this.cd.detectChanges();
            this.core.api.user_validate(validationCode).then((result) => {
                if (result.success) {
                    this.waitingSpinning = false;
                    this.core.api.interaction(InteractionType.Login, InteractionComponent.ValidatePage);
                    this.core.api.interaction(InteractionType.ValidateEmail, InteractionComponent.ValidatePage);
                    this.core.toast.toastSuccess({
                        header: "Validation Success",
                        message: "Thank you for validating your email. Now enjoy your FREE offers!",
                        onHide: () => {
                            this.core.nav.navPageSearching(this.core.api, this.offerId);
                        }
                    });
                } else {
                    this.waiting = false;
                    this.core.toast.toastError({
                        header: "Unsuccessful Validation",
                        message: "The validation code you entered was incorrect. Please check your email and try again, or request a new validation code."
                    });
                }
                this.cd.detectChanges();
            }, (reason) => {
                this.waiting = false;
                this.core.toast.toastError({
                    header: "Unsuccessful Validation",
                    message: "The validation code you entered was incorrect. Please check your email and try again, or request a new validation code."
                });
                this.cd.detectChanges();
            });
        }
    }

    onResendValidationCode() {
        this.core.dialogs.confirm({
            title: "Re-send Validation Code",
            message: "Would you like us to send another email validation code to " + this.email + "?",
            okButtonText: "Send",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                if (this.core.net.connectionType === NetworkConnectionType.None) {
                    this.handleNoData("resend");
                } else {
                    this.waiting = true;
                    this.cd.detectChanges();
                    this.core.api.user_resendValidationCode(this.email).then((successresult) => {
                        this.waiting = false;
                        this.cd.detectChanges();
                        if (!successresult.success) {
                            this.core.toast.toastError({
                                header: "Problem Re-sending Code",
                                message: "There was a problem re-sending your validation code."
                            });
                        }
                    }, (error) => {
                        this.waiting = false;
                        this.cd.detectChanges();
                        this.core.toast.toastError({
                            header: "Problem Re-sending Code",
                            message: "There was a problem re-sending your validation code."
                        });
                    });
                }
            }
        });
    }

    onAbortAccountValidation(): void {
        this.core.dialogs.confirm({
            title: "Abort Validation",
            message: "Would you like to abort email validation?",
            okButtonText: "Abort Validation",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                this.core.api.user_abortValidation();
                this.goBack();
            }
        });
    }

    private handleNoData(type: "validate" | "resend") {
        if (type === "validate") {
            this.core.toast.toastError({
                header: "No Data",
                message: "Sorry! We can't validate your code without data or wi-fi available.\n\nPlease enable data and try again."
            });
        } else if (type === "resend") {
            this.core.toast.toastError({
                header: "No Data",
                message: "Sorry! We can't send another validation code without data or wi-fi available.\n\nPlease enable data and try again."
            });
        }
    }
}
